import { useEffect, useState, useRef } from 'react'
import { Center, Tag, Text, VStack, Wrap } from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import { useLocation } from 'react-router-dom'
import { IOption, IOptionWithIcon, IOptionWithAvatar } from 'types/common'
import CustomInputDropdown from 'components/CustomInputDropdown'
import { filterValueInArray } from 'components/Pages/UserPage/UserListFilterDialog/utils'
import SvgIcon from 'components/SvgIcon'
import { checkValidArray, getValidArray } from 'utils/common'
import {
  filterCurrentItems,
  filterRemainItem,
  getOptionSelect
} from '../../../../pages/CollectionsPage/components/FilterModal/utils'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'
import { IAsyncSelectOptionWithIcon } from 'interfaces/common'

interface IFilterInputProps<T> {
  isOpenModal: boolean
  name: string
  label: string
  placeholder: string
  storeOptions: T[]
  filteredOptions?: IAsyncSelectOptionWithIcon<number>[]
  selectedOptions: IOptionWithIcon<string>[]
  setSelectedOptions: (value: IOptionWithIcon<string>[]) => void
  hasIcon?: boolean
  hasAvatar?: boolean
  buildOptions?: (items: T[]) => IOptionWithAvatar<string>[]
}

const FilterInput = <T extends { id?: number; name?: string }>(props: IFilterInputProps<T>) => {
  const {
    isOpenModal,
    name,
    label,
    placeholder,
    storeOptions,
    filteredOptions,
    selectedOptions,
    setSelectedOptions,
    hasIcon,
    hasAvatar,
    buildOptions
  } = props
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const { authStore } = useStores()
  const { userDetail } = authStore
  const [options, setOptions] = useState<IOptionWithIcon<string>[]>(buildOptions ? buildOptions(storeOptions) : getOptionSelect<T>(storeOptions))
  const prevIsOpenModalRef = useRef<boolean>(false)

  const organizationId: number = userDetail?.organizationId ?? 0

  function chooseAvailableOption(value: string) {
    const selectedItems: IOptionWithIcon<string> | undefined = getValidArray(options).find(
      (option: IOptionWithIcon<string>) => option?.value === value
    )
    if (selectedItems) {
      setSelectedOptions([...getValidArray(selectedOptions), selectedItems])
    }
  }

  function removeSelectedOption(value: string) {
    const removedItems: IOptionWithIcon<string> | undefined = getValidArray(selectedOptions).find(
      (option: IOptionWithIcon<string>) => option?.value === value
    )
    if (removedItems) {
      const remainItems: IOption<string>[] = filterValueInArray(value, selectedOptions)
      setSelectedOptions(remainItems)
    }
  }

  function prefillFormData() {
    const currentItems = filterCurrentItems<T>(params, name, storeOptions)
    setSelectedOptions(getOptionSelect<T>(currentItems))
  }

  useEffect(() => {
    if (!filteredOptions) {
      prefillFormData()
      if (isOpenModal) {
        setOptions(buildOptions ? buildOptions(storeOptions) : getOptionSelect<T>(storeOptions))
      }
    }
  }, [isOpenModal, organizationId, filteredOptions])

  useEffect(() => {
    const isJustOpened = isOpenModal && !prevIsOpenModalRef.current
    prevIsOpenModalRef.current = isOpenModal

    if (isOpenModal) {
      if (isJustOpened && filteredOptions && filteredOptions.length > 0) {
        const filteredItems = getValidArray(filteredOptions).map(option => ({
          label: option?.label ?? '',
          value: String(option?.value ?? ''),
          icon: option?.icon
        }))
        setSelectedOptions(filteredItems)
      }
      setOptions(buildOptions ? buildOptions(storeOptions) : getOptionSelect<T>(storeOptions))
    }
  }, [isOpenModal, filteredOptions, storeOptions])

  useEffect(() => {
    const remainItems = filterRemainItem<T>(storeOptions, selectedOptions)
    setOptions(buildOptions ? buildOptions(remainItems) : getOptionSelect<T>(remainItems))
  }, [selectedOptions])

  return (
    <VStack width="full" alignItems="flex-start" spacing={3}>
      <CustomInputDropdown
        name={`${name}`}
        label={label}
        placeholder={placeholder}
        optionsData={options}
        selectedData={selectedOptions}
        chooseOptionsHandler={chooseAvailableOption}
        hasIcon={hasIcon}
        hasAvatar={hasAvatar}
      />
      <Wrap>
        {getValidArray(selectedOptions).map(option => (
          <Tag key={option?.value} size="lg" gap={1} color="gray.700" border="1px solid #E2E8F0">
            {hasIcon && option?.icon && <IconBuilder icon={option?.icon} isActive size={16} />}
            {option?.label}
            <SvgIcon
              iconName="ic_baseline-close"
              size={12}
              cursor="pointer"
              onClick={() => removeSelectedOption(option?.value)}
            />
          </Tag>
        ))}
        {checkValidArray(selectedOptions) && (
          <Center>
            <Text as="u" fontSize="md" color="gray.600" cursor="pointer" onClick={() => setSelectedOptions([])}>
              Clear all
            </Text>
          </Center>
        )}
      </Wrap>
    </VStack>
  )
}

export default FilterInput
